/** @jsx jsx */

import { Box } from '@balance-web/box';
import { Button } from '@balance-web/button';
import { jsx } from '@balance-web/core';
import { Heading } from '@balance-web/heading';
import { useTheme } from '@balance-web/theme';
import { makeId, useId } from '@balance-web/utils';
import { Text } from '@balance-web/text';
import type { FormEvent, ReactElement, ReactNode } from 'react';
import { Flex } from '@balance-web/flex';

import type { ModalSize } from './size';
import { useModalWidth } from './size';
import { DialogBase } from './DialogBase';

type Action = {
  action: () => void;
  label: string;
};

type SubmitAction = {
  action: (event: FormEvent<HTMLFormElement>) => void;
  label: string;
  // Override the button render
  children?: ReactNode;
  loading?: boolean;
  disabled?: boolean;
};

type FormDialogProps = {
  actions: {
    cancel: Action;
    confirm: SubmitAction;
  };
  id?: string;
  isOpen: boolean;
  children: React.ReactNode;
  title?: string;
  subTitle?: string;
  size?: ModalSize;
  autoFocus?: boolean;
  header?: ReactElement;
  showSectionDivider?: boolean;
};

export const FormDialog = ({
  actions,
  isOpen,
  children,
  title,
  subTitle,
  id,
  size = 'small',
  autoFocus = true,
  header,
  showSectionDivider = false,
}: FormDialogProps) => {
  const { cancel, confirm } = actions;
  const theme = useTheme();
  const instanceId = useId(id);
  const headingId = makeId(instanceId, 'heading');

  const modalWidth = useModalWidth(size);

  const onClose = () => {
    cancel.action();
  };

  const formId = makeId(instanceId, 'form');
  const borderStyle = `1px solid ${theme.palette.global.border}`;
  return (
    <DialogBase
      aria-labelledby={headingId}
      autoFocus={autoFocus}
      elevation="modal"
      id={instanceId}
      isOpen={isOpen}
      width={modalWidth}
      onClose={onClose}
    >
      <Box
        as="form"
        id={formId}
        paddingX="xxlarge"
        onSubmit={(e) => {
          e.preventDefault();
          confirm.action(e);
        }}
      >
        {header ? (
          <div
            css={
              showSectionDivider && {
                borderBottom: borderStyle,
                '@media print': { borderBottom: '0px' },
              }
            }
          >
            {header}
          </div>
        ) : (
          <Box paddingBottom="large" paddingTop="xlarge">
            <Heading id={headingId} level="3">
              {title}
            </Heading>

            {subTitle ? (
              <Text color="muted" size="small">
                {subTitle}
              </Text>
            ) : null}
          </Box>
        )}

        <Box paddingY="xlarge">
          {typeof children === 'string' ? (
            <Text color="muted">{children}</Text>
          ) : (
            children
          )}
        </Box>

        <div
          css={{
            display: 'flex',
            justifyContent: 'start',
            flexWrap: 'wrap',
            flexDirection: 'row',
            paddingTop: theme.spacing.large,
            paddingBottom: theme.spacing.xxlarge,
            gap: theme.spacing.large,
          }}
        >
          <Button
            colorScheme="tertiary"
            disabled={confirm.loading}
            id={makeId(instanceId, 'cancel')}
            label={cancel.label}
            size="small"
            variant="text"
            onClick={cancel.action}
          />
          <Flex flex="1" justifyContent="end">
            <Button
              children={confirm.children}
              disabled={confirm.disabled}
              id={formId}
              label={confirm.label}
              loading={confirm.loading}
              size="small"
              type="submit"
            />
          </Flex>
        </div>
      </Box>
    </DialogBase>
  );
};
